import { Box, Container, Grid } from "@mui/material"
import React, { useEffect, useState } from "react"
import { useTranslation } from "react-i18next"
import CheckBoxGroupField from "src/components/atom/CheckBoxGroupField"

import { OutlinedButton } from "src/components/buttons/OutlinedButton"
import { Title } from "src/components/layout/Title"
import { useLoginContext } from "src/hooks/useLoginContext"
import "src/styles/common.scss"
import { useNavigate } from "react-router-dom"
import { AdviserItem } from "./components/AdviserItem"
import { Adviser, ProfileService } from "src/openapi"
import { useRecoilState } from "recoil"
import { ShowToast } from "src/states/showToast"
import { RESPONSE_CODE, SNACKBAR_TYPES, SORT_ORDER } from "src/modules/const"
import { Typography } from "src/UILibrary"
import { useMediaQuery } from "react-responsive"

const PAGE_SIZE = 9

export const AdviserPage = () => {
  const { t } = useTranslation("translation", { keyPrefix: "adviser" })

  const isPC = useMediaQuery({ query: "(min-width: 1160px)" })

  const [, setShowToast] = useRecoilState(ShowToast)

  const navigate = useNavigate()

  const { areas, jobs } = useLoginContext()

  const [areaID, setAreaID] = useState<number[]>([])
  const [selectedJobs, setSelectedJobs] = useState<number[]>([])

  const [advisers, setAdvisers] = useState<Adviser[]>([])
  const [total, setTotal] = useState<number>(0)

  const [residentAdvisers, setResidentAdvisers] = useState<Adviser[]>([])

  const handleItemSelected = (item: Adviser) => {
    navigate("/adviser/detail", { state: { adviserID: item.userId, userID: item.id } })
  }

  const handleGetAdvisers = (refresh?: boolean) => {
    ProfileService.listAdviser(
      undefined,
      refresh ? 0 : advisers.length,
      PAGE_SIZE,
      areaID.join(","),
      selectedJobs.join(","),
      false,
      "created_at",
      SORT_ORDER.ASCENDING
    )
      .then((res) => {
        if (res.status === RESPONSE_CODE.SUCCESS) {
          if (refresh) {
            setAdvisers(res.data?.advisers as Adviser[])
          } else {
            setAdvisers([...advisers, ...(res.data?.advisers as Adviser[])])
          }

          setTotal(res.data?.count as number)
        } else {
          setShowToast({ show: true, text: t("failed_list"), type: SNACKBAR_TYPES.ERROR })
        }
      })
      .catch(() => {
        setShowToast({ show: true, text: t("failed_list"), type: SNACKBAR_TYPES.ERROR })
      })
  }

  const handleGetResidentAdvisers = (refresh?: boolean) => {
    ProfileService.listAdviser(
      undefined,
      refresh ? 0 : advisers.length,
      PAGE_SIZE,
      areaID.join(","),
      selectedJobs.join(","),
      true,
      "created_at",
      SORT_ORDER.ASCENDING
    )
      .then((res) => {
        if (res.status === RESPONSE_CODE.SUCCESS) {
          if (refresh) {
            setResidentAdvisers(res.data?.advisers as Adviser[])
          } else {
            setResidentAdvisers([...advisers, ...(res.data?.advisers as Adviser[])])
          }
        } else {
          setShowToast({ show: true, text: t("failed_list"), type: SNACKBAR_TYPES.ERROR })
        }
      })
      .catch(() => {
        setShowToast({ show: true, text: t("failed_list"), type: SNACKBAR_TYPES.ERROR })
      })
  }

  useEffect(() => {
    handleGetAdvisers(true)
    handleGetResidentAdvisers(true)
  }, [])

  const handleSearch = () => {
    handleGetAdvisers(true)
    handleGetResidentAdvisers(true)
  }

  return (
    <>
      <Title title={t("title")} sub_title={t("adviser_list")} />
      <Container maxWidth="lg" sx={{ p: 0 }} className="main-body-with-footer">
        <Box width="100%" sx={{ p: "20px" }}>
          <CheckBoxGroupField
            name={t("area") + "："}
            datas={areas}
            selected={areaID}
            handleSelected={setAreaID}
            labelSize={14}
            alignHorizontal={isPC}
          />
          <CheckBoxGroupField
            name={t("job") + "："}
            selected={selectedJobs}
            handleSelected={setSelectedJobs}
            datas={jobs}
            alignHorizontal={isPC}
            sx={{ mt: "20px" }}
            labelSize={14}
          />
          <OutlinedButton
            caption={t("search")}
            sx={{ mt: "40px" }}
            onClick={() => handleSearch()}
          />
        </Box>
        <Typography.Large sx={{ fontWeight: "400", ml: "30px" }}>
          {t("resident_adviser")}
        </Typography.Large>
        {residentAdvisers.length === 0 ? (
          <Box sx={{ display: "flex", my: "50px", width: "100%" }}>
            <Typography.SubTitleNormal
              sx={{ mx: "auto", fontFamily: "Noto Sans JP !important", color: "gray" }}
            >
              {t("no_data")}
            </Typography.SubTitleNormal>
          </Box>
        ) : (
          <Box maxWidth="md" sx={{ mt: "10px", mb: "60px", mx: "auto" }}>
            <Grid container spacing={2}>
              {residentAdvisers.map((adviser, index) => {
                return (
                  <Grid item xs={12} sm={4} md={4} key={index}>
                    <AdviserItem item={adviser} handleClick={handleItemSelected} />
                  </Grid>
                )
              })}
            </Grid>
          </Box>
        )}
        <Typography.Large sx={{ fontWeight: "400", ml: "30px" }}>
          {t("general_adviser")}
        </Typography.Large>
        {advisers.length === 0 ? (
          <Box sx={{ display: "flex", my: "50px", width: "100%" }}>
            <Typography.SubTitleNormal
              sx={{ mx: "auto", fontFamily: "Noto Sans JP !important", color: "gray" }}
            >
              {t("no_data")}
            </Typography.SubTitleNormal>
          </Box>
        ) : (
          <Box maxWidth="md" sx={{ mt: "10px", mb: "60px", mx: "auto" }}>
            <Grid container spacing={2}>
              {advisers.map((adviser, index) => {
                return (
                  <Grid item xs={12} sm={4} md={4} key={index}>
                    <AdviserItem item={adviser} handleClick={handleItemSelected} />
                  </Grid>
                )
              })}
            </Grid>
            {total !== advisers.length && (
              <OutlinedButton
                caption={t("more")}
                sx={{ mt: "20px", mx: "auto", display: "flex", justifyContent: "center" }}
                onClick={() => handleGetAdvisers()}
              />
            )}
          </Box>
        )}
      </Container>
    </>
  )
}
